/**
 * The top of the domain page in the owner's v2.1 spec (docs/design/registrar-ops-console-v2.1-domain-detail.html):
 * breadcrumb, the name with its TLD and a copy button, the ID line, "Tên miền đang xem" (the
 * customer's other domains) and "Xuất thông tin"; under it the status ribbon (six states, each
 * opening the tab that explains it) and the tab grid (a select on phones).
 */

import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Check,
  Clock,
  Copy,
  FileText,
  FolderOpen,
  Globe,
  History,
  LayoutGrid,
  Receipt,
  Server,
  ShieldCheck,
  Tag,
  Upload,
  UserRound,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api, type DomainDetail, type RegistryTransaction } from '../../api';
import type { DomainRegistryState } from '../../api/areas/core';
import { Badge } from '../../components/Badge';
import { Crumbs, useHubCrumbs } from '../../components/PageHeader';
import { useApi } from '../../lib/useApi';
import { CustomerRef } from './refs';
import { dnssecCell, registryCell, syncCell, type RibbonCell } from './ribbon';
import { BILLING, DOSSIER, LIFECYCLE, useStatusLabel, type Meta } from './status';
import { DOMAIN_TAB_IDS, domainTabUrl, type DomainTabId } from './tabs/moved';

const selectClass =
  'h-9 min-w-0 rounded-md border border-input bg-card px-3 py-1 text-[13px] shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

export const TAB_ICONS: Record<DomainTabId, LucideIcon> = {
  overview: LayoutGrid,
  subject: UserRound,
  dossier: FolderOpen,
  dns: Globe,
  dnssec: ShieldCheck,
  addons: Tag,
  finance: Receipt,
  lifecycle: Clock,
  transfer: ArrowLeftRight,
  registry: Server,
  compliance: FileText,
  history: History,
};

/** Copies the name; shows a tick for a moment. */
function CopyName({ value }: { value: string }) {
  const { t } = useTranslation('domainPage');
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-muted-foreground"
      onClick={() =>
        navigator.clipboard?.writeText(value).then(
          () => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
          },
          () => undefined,
        )
      }
    >
      {done ? <Check /> : <Copy />} {done ? t('header.copied') : t('header.copy')}
    </Button>
  );
}

/** "Tên miền đang xem": the other domains of the same customer (first 100 by name). */
function DomainPicker({ detail, tab }: { detail: DomainDetail; tab: DomainTabId }) {
  const { t } = useTranslation('domainPage');
  const navigate = useNavigate();
  const d = detail.domain;
  const customer = useApi((signal) => api.getCustomerOverview(d.customer_id, { signal }), [d.customer_id]);
  const domains = customer.status === 'success' ? customer.data.domains : [];
  if (domains.length < 2) return null;
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-muted-foreground max-sm:flex-row max-sm:items-center max-sm:justify-between max-sm:gap-3">
      <span>{t('header.picker')}</span>
      <select className={cn(selectClass, 'max-w-64 max-sm:flex-1')} value={d.ascii_name} onChange={(e) => void navigate(domainTabUrl(e.target.value, tab))}>
        {domains.map((x) => (
          <option key={x.id} value={x.ascii_name}>
            {x.unicode_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** "Xuất thông tin": the page's record as a JSON file (what staff already see; no identity numbers). */
function exportDetail(detail: DomainDetail) {
  const blob = new Blob([JSON.stringify(detail, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${detail.domain.ascii_name}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function DomainHeader({ detail, tab = 'overview' }: { detail: DomainDetail; tab?: DomainTabId }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const d = detail.domain;
  const trail = useHubCrumbs([{ label: td('list.title'), to: '/domains' }, { label: d.unicode_name }], d.unicode_name);
  const meta: ReactNode[] = [
    d.unicode_name !== d.ascii_name ? (
      <span key="a" className="font-mono">
        {d.ascii_name}
      </span>
    ) : null,
    d.subject_display_name ? <span key="s">{d.subject_display_name}</span> : null,
    <span key="r">{d.registry_name ?? d.registry}</span>,
    <CustomerRef key="c" id={d.customer_id} name={d.customer_name} />,
  ].filter(Boolean);
  return (
    <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-domain-header>
      <div className="min-w-0 space-y-2">
        <Crumbs crumbs={trail} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-[26px] leading-tight font-bold tracking-tight break-all xl:text-[30px]">{d.unicode_name}</h1>
          <Badge tone="info" icon={false}>
            .{d.tld}
          </Badge>
          <CopyName value={d.unicode_name} />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {meta.map((m, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              {i > 0 ? <span aria-hidden="true">·</span> : null}
              {m}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2.5 max-sm:flex-col max-sm:items-stretch">
        <DomainPicker detail={detail} tab={tab} />
        <Button type="button" variant="outline" size="sm" className="max-lg:hidden" onClick={() => exportDetail(detail)}>
          <Upload /> {t('header.export')}
        </Button>
      </div>
    </header>
  );
}

/** One state of the ribbon: label, badge, the tab that explains it. */
function Cell({ label, meta, to }: { label: string; meta: Meta; to: string }) {
  return (
    <Link to={to} className="flex min-w-0 flex-col items-start gap-2 px-4 hover:bg-muted/40 max-md:px-3 md:border-r md:last:border-r-0">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      <Badge tone={meta.tone} className="max-w-full whitespace-normal">
        {meta.label}
      </Badge>
    </Link>
  );
}

/**
 * Six states at a glance (v2.1 "Dịch vụ · Registry · Hồ sơ · Thanh toán · Đồng bộ · DNSSEC"), each
 * from the domain's own data: lifecycle, registry commands and statuses, dossier, billing,
 * VNNIC dossier sync (.vn) or open registry discrepancies, and the DS records at the registry.
 */
export function StatusRibbon({
  detail,
  txs,
  sync: state,
  dsCount,
}: {
  detail: DomainDetail;
  /** `unavailable` when the commands could not be read (refused to the role, or failed). */
  txs: readonly RegistryTransaction[] | 'unavailable' | null;
  /** The registry's last answer and the discrepancies found; `unavailable` when it could not be read. */
  sync: DomainRegistryState | 'unavailable' | null;
  /** `unavailable` when the delegation could not be read. */
  dsCount: number | 'unavailable' | null;
}) {
  const { t } = useTranslation('domainPage');
  const label = useStatusLabel();
  const d = detail.domain;
  const url = (tab: DomainTabId, section?: string) => domainTabUrl(d.ascii_name, tab, section);
  const meta = (map: Record<string, Meta>, value: string): Meta => ({ label: label(map, value), tone: map[value]?.tone ?? 'neutral' });
  const stopped = detail.registration?.state === 'MANUAL_REVIEW';
  const cell = (c: RibbonCell): Meta => ({ label: 'status' in c ? c.status : t(`ribbon.${c.key}`, { count: c.count }), tone: c.tone });
  const registry = cell(registryCell(txs, detail.registry_statuses));
  const sync = cell(syncCell(state));
  const dnssec = cell(dnssecCell(dsCount));
  return (
    <div className="mb-4 grid grid-cols-3 gap-y-4 rounded-xl border bg-card py-4 md:grid-cols-6" data-status-ribbon>
      <Cell label={t('ribbon.service')} meta={stopped ? { label: t('ribbon.stopped'), tone: 'serious' } : meta(LIFECYCLE, d.lifecycle_state)} to={url('lifecycle')} />
      <Cell label={t('ribbon.registry')} meta={registry} to={url('registry')} />
      <Cell label={t('ribbon.dossier')} meta={meta(DOSSIER, detail.dossier?.status ?? d.dossier_state)} to={url('dossier')} />
      <Cell label={t('ribbon.billing')} meta={meta(BILLING, d.billing_state)} to={url('finance')} />
      <Cell label={t('ribbon.sync')} meta={sync} to={url('compliance')} />
      <Cell label={t('ribbon.dnssec')} meta={dnssec} to={url('dnssec')} />
    </div>
  );
}

/** The twelve tabs as a grid (six, then four per row), a select on phones. `counts` adds a small figure. */
export function TabGrid({ detail, active, counts }: { detail: DomainDetail; active: DomainTabId; counts?: Partial<Record<DomainTabId, string>> }) {
  const { t } = useTranslation('domainPage');
  const navigate = useNavigate();
  const name = detail.domain.ascii_name;
  return (
    <>
      <nav aria-label={t('tabs.label')} className="mb-5 hidden grid-cols-4 gap-1 rounded-xl border bg-card p-1.5 sm:grid xl:grid-cols-6" data-tab-grid>
        {DOMAIN_TAB_IDS.map((id) => {
          const Icon = TAB_ICONS[id];
          const on = id === active;
          return (
            <Link
              key={id}
              to={domainTabUrl(name, id)}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'flex min-h-9 min-w-0 items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-[12.5px] leading-snug font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-accent-foreground',
                on && 'border-primary/20 bg-accent font-semibold text-accent-foreground',
              )}
            >
              <Icon className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0">{t(`tabs.${id}`)}</span>
              {counts?.[id] ? <span className="rounded bg-primary/10 px-1 text-[10px] tabular">{counts[id]}</span> : null}
            </Link>
          );
        })}
      </nav>
      <label className="mb-4 flex items-center justify-between gap-3 rounded-lg border bg-card p-3 text-xs text-muted-foreground sm:hidden">
        <span>{t('tabs.mobile')}</span>
        <select className={cn(selectClass, 'max-w-[72%] flex-1 text-accent-foreground')} value={active} onChange={(e) => void navigate(domainTabUrl(name, e.target.value as DomainTabId))}>
          {DOMAIN_TAB_IDS.map((id) => (
            <option key={id} value={id}>
              {t(`tabs.${id}`)}
              {counts?.[id] ? ` · ${counts[id]}` : ''}
            </option>
          ))}
        </select>
      </label>
    </>
  );
}
